import React, { useCallback, useEffect, useState, useRef } from 'react';
import { Box, Flex, Input, Button, IconButton, getPaletteColor, Card, theme } from 'pcln-design-system';
import styled from 'styled-components';
import MicIcon from '../../svgs/MicIcon';
import broadcast, { BroadcastType, usePennySubscriber } from '../../utils/pennyBroadcast';
import StopCircleIcon from '../../svgs/StopCircleIcon';
import { messageInputConfig } from '../../constants';
import VoiceControlButton from '../Realtime/VoiceControlButton/VoiceControlButton';
import { useRealTimeContext } from '../../hooks/useRealTimeContext';
const UserInputContainer = /*#__PURE__*/styled(Flex).withConfig({
  displayName: "UserInputBar__UserInputContainer",
  componentId: "sc-imtymz-0"
})(["", " padding:0 8px;align-items:center;background-color:", ";justify-content:space-between;width:", ";"], props => {
  if (props.isRecording) {
    return `
        height: 67px;
        margin: 0px;
        border-top: 1px solid ${getPaletteColor('border.tint')(props)};
        border-radius: 0;
      `;
  } else {
    return `
        height: 67px;
        margin: 1px;
        border-radius: 0 0 24px 24px;
      `;
  }
}, props => getPaletteColor('background.lightest')(props), props => {
  var _a;
  return ((_a = props.styles) === null || _a === void 0 ? void 0 : _a.inputGradientFocus) ? '100%' : '';
});
const IconButtonWrapper = /*#__PURE__*/styled(IconButton).withConfig({
  displayName: "UserInputBar__IconButtonWrapper",
  componentId: "sc-imtymz-1"
})(["z-index:1000;"]);
const MicContainer = /*#__PURE__*/styled(Box).withConfig({
  displayName: "UserInputBar__MicContainer",
  componentId: "sc-imtymz-2"
})(["height:49px;width:49px;z-index:1000;"]);
function UserInputBar(props) {
  const {
    limiterCheck = () => {},
    styles
  } = props;
  const [textInput, setTextInput] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  const inputRef = useRef(null);
  const {
    isRealtime
  } = useRealTimeContext();
  const {
    MAXLENGTH
  } = messageInputConfig;
  function pennySubscriptionHandler(event) {
    if (event.data.type === BroadcastType.USER_IS_SILENT) {
      props.onStopRecording();
    } else if (event.data.type === BroadcastType.VOICE_RECORDING_STARTED) {
      setIsRecording(true);
    } else if (event.data.type === BroadcastType.VOICE_RECORDING_STOPPED) {
      setIsRecording(false);
    } else if (event.data.type === BroadcastType.AUDIO_PLAYBACK_STARTED) {
      setIsAudioPlaying(true);
    } else if (event.data.type === BroadcastType.AUDIO_QUEUE_DRAINED) {
      setIsAudioPlaying(false);
    }
  }
  const pennyBroadcastListener = useCallback(pennySubscriptionHandler, []);
  usePennySubscriber(pennyBroadcastListener);
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);
  useEffect(() => {
    limiterCheck(textInput.length);
  }, [textInput, isRecording, props.isLoading]);
  function handleTextChange(e) {
    setTextInput(e.target.value);
  }
  function handleTextSubmit() {
    if (props.isLoading || props.isDisablingInput || !textInput) {
      return;
    }
    setTextInput('');
    if (props.onTextSubmit) {
      props.onTextSubmit(textInput);
    }
  }
  function onKeyUpHandler(event) {
    if (event.key === 'Enter') {
      handleTextSubmit();
    }
  }
  function handleRecordButtonClick() {
    if (props.onRecord) {
      props.onRecord();
    }
  }
  function haltPlayback() {
    broadcast({
      type: BroadcastType.INTERRUPT_AUDIO_PLAYBACK,
      sender: 'UserInputBar'
    });
    if (props.onStopPlayback) {
      props.onStopPlayback();
    }
  }
  const [isFocused, setIsInputFocused] = useState(false);
  function handleInputFocus() {
    setIsInputFocused(true);
  }
  function handleInputBlur() {
    setIsInputFocused(false);
  }
  const inputColor = textInput.length >= MAXLENGTH ? theme.colors.red : theme.colors.blue;
  const inputStyle = (styles === null || styles === void 0 ? void 0 : styles.inputGradientFocus) ? isFocused ? Object.assign({}, styles === null || styles === void 0 ? void 0 : styles.inputGradientFocus) : Object.assign({}, styles === null || styles === void 0 ? void 0 : styles.inputGradientNoFocus) : {
    borderStyle: 'none',
    borderRadius: '24px',
    boxShadow: `0 0 0 2px ${inputColor}`
  };
  return React.createElement(UserInputContainer, {
    isRecording: isRecording
  }, React.createElement(Card, {
    mr: 2,
    width: '100%',
    borderRadius: 24,
    boxShadowSize: 'sm'
  }, React.createElement(Input, {
    onFocus: handleInputFocus,
    onBlur: handleInputBlur,
    style: inputStyle,
    maxLength: MAXLENGTH,
    disabled: props.isDisablingInput,
    id: 'chatInput',
    "data-testid": 'chatInput',
    autoComplete: 'off',
    onChange: handleTextChange,
    value: textInput,
    placeholder: 'Ask Penny a question',
    onKeyUp: onKeyUpHandler,
    mr: 2,
    ref: inputRef
  })), textInput && React.createElement(Button, {
    onClick: handleTextSubmit,
    "data-testid": 'chatSubmitButton',
    mr: 2,
    height: '49px'
  }, "Submit"), isRealtime ? React.createElement(VoiceControlButton, null) : React.createElement(MicContainer, null, isRecording ? React.createElement(IconButtonWrapper, {
    onClick: props.onStopRecording,
    "data-testid": 'stopRecordingButton',
    icon: React.createElement(StopCircleIcon, null)
  }) : isAudioPlaying ? React.createElement(IconButtonWrapper, {
    height: '49px',
    width: '49px',
    onClick: haltPlayback,
    "data-testid": 'stopPlaybackButton',
    icon: React.createElement(StopCircleIcon, null)
  }) : React.createElement(IconButtonWrapper, {
    height: '49px',
    width: '49px',
    onClick: handleRecordButtonClick,
    "data-testid": 'recordButton',
    icon: React.createElement(MicIcon, null)
  })));
}
export default UserInputBar;